import { useEffect, useMemo, useState } from 'react';
import { getAuthDepartments, type AuthDepartmentPerson } from '@/api/auth';
import DatePicker from '@/components/base/DatePicker';
import DateTimePicker from '@/components/base/DateTimePicker';
import SearchableSelect from '@/components/base/SearchableSelect';

export interface OrderMetaEditValue {
  departmentId: string;
  departmentTitle: string;
  accountId: string;
  accountName: string;
  /** Y-m-d H:i:s */
  createdAt: string;
}

interface OrderMetaEditFieldsProps {
  value: OrderMetaEditValue;
  onChange: (next: OrderMetaEditValue) => void;
  disabled?: boolean;
  /** inline：表格头一行；grid：信息卡片网格 */
  layout?: 'inline' | 'grid';
}

export default function OrderMetaEditFields({
  value,
  onChange,
  disabled = false,
  layout = 'inline',
}: OrderMetaEditFieldsProps) {
  const [deptOptions, setDeptOptions] = useState<string[]>([]);
  const [deptIdMap, setDeptIdMap] = useState<Record<string, string>>({});
  const [personsByDept, setPersonsByDept] = useState<Record<string, AuthDepartmentPerson[]>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const res = await getAuthDepartments();
        const ok = res.error === false || Number(res.error) === 0;
        if (cancelled || !ok || !Array.isArray(res.data)) return;

        const nextIdMap: Record<string, string> = {};
        const nextPersons: Record<string, AuthDepartmentPerson[]> = {};
        const nextOptions: string[] = [];

        res.data.forEach((item) => {
          const id = Number(item.id);
          const title = String(item.title || '').trim();
          if (!title || !Number.isFinite(id) || id <= 0) return;
          if (nextIdMap[title]) return;
          nextIdMap[title] = String(id);
          nextOptions.push(title);
          nextPersons[title] = Array.isArray(item.persons)
            ? item.persons
                .map((p) => ({
                  id: p.id,
                  account: p.account,
                  name: String(p.name || p.account || '').trim(),
                  phone: p.phone,
                }))
                .filter((p) => !!p.name)
            : [];
        });

        // 保证当前订单科室出现在选项中
        if (value.departmentTitle && !nextIdMap[value.departmentTitle] && value.departmentId) {
          nextIdMap[value.departmentTitle] = value.departmentId;
          nextOptions.unshift(value.departmentTitle);
          nextPersons[value.departmentTitle] = nextPersons[value.departmentTitle] || [];
        }

        setDeptIdMap(nextIdMap);
        setDeptOptions(nextOptions);
        setPersonsByDept(nextPersons);
      } catch {
        if (!cancelled) {
          setDeptOptions(value.departmentTitle ? [value.departmentTitle] : []);
          setDeptIdMap(
            value.departmentTitle && value.departmentId
              ? { [value.departmentTitle]: value.departmentId }
              : {},
          );
          setPersonsByDept({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // 仅挂载时拉一次科室列表
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const personOptions = useMemo(() => {
    const list = personsByDept[value.departmentTitle] || [];
    const names = list.map((p) => p.name);
    if (value.accountName && !names.includes(value.accountName)) {
      return [value.accountName, ...names];
    }
    return names;
  }, [personsByDept, value.departmentTitle, value.accountName]);

  const handleDeptChange = (next: string | string[]) => {
    const title = Array.isArray(next) ? next[0] || '' : next || '';
    const deptId = title ? String(deptIdMap[title] || '') : '';
    const persons = personsByDept[title] || [];

    // 科室未变更时保留当前下单人
    if (title === value.departmentTitle) {
      onChange({
        ...value,
        departmentTitle: title,
        departmentId: deptId || value.departmentId,
      });
      return;
    }

    // 切换科室：自动选中该科室人员列表第一位
    const firstPerson = persons[0];
    onChange({
      ...value,
      departmentTitle: title,
      departmentId: deptId,
      accountId: firstPerson ? String(firstPerson.id) : '',
      accountName: firstPerson ? firstPerson.name : '',
    });
  };

  const handlePersonChange = (next: string | string[]) => {
    const name = Array.isArray(next) ? next[0] || '' : next || '';
    const persons = personsByDept[value.departmentTitle] || [];
    const hit = persons.find((p) => p.name === name);
    onChange({
      ...value,
      accountName: name,
      accountId: hit ? String(hit.id) : value.accountId && name === value.accountName ? value.accountId : '',
    });
  };

  const handleCreatedAtChange = (next: string) => {
    onChange({
      ...value,
      createdAt: next,
    });
  };

  const handleCreatedDateChange = (nextDate: string) => {
    if (!nextDate) {
      onChange({ ...value, createdAt: '' });
      return;
    }
    const time = /[ T](\d{2}:\d{2}:\d{2})/.exec(String(value.createdAt || '').trim());
    onChange({
      ...value,
      createdAt: time ? `${nextDate} ${time[1]}` : `${nextDate} 00:00:00`,
    });
  };

  const fieldClass = 'flex flex-col gap-1 min-w-0';

  const labelEl = (text: string) =>
    layout === 'grid' ? (
      <span className="text-foreground-500 text-xs">{text}</span>
    ) : (
      <span className="text-xs text-foreground-500">{text}</span>
    );

  return (
    <div
      className={
        layout === 'grid'
          ? 'contents'
          : 'flex items-end gap-3 flex-wrap'
      }
    >
      <div className={fieldClass}>
        {labelEl(layout === 'grid' ? '使用科室' : '科室')}
        <div className={layout === 'grid' ? 'w-full max-w-[220px]' : 'w-[180px]'}>
          <SearchableSelect
            options={deptOptions}
            value={value.departmentTitle}
            onChange={handleDeptChange}
            placeholder={loading ? '加载科室中...' : '请选择科室'}
            allLabel="请选择科室"
            searchPlaceholder="搜索科室..."
            icon="ri-building-line"
          />
        </div>
      </div>

      <div className={fieldClass}>
        {labelEl(layout === 'grid' ? '采购人' : '下单人')}
        <div className={layout === 'grid' ? 'w-full max-w-[200px]' : 'w-[160px]'}>
          <SearchableSelect
            options={personOptions}
            value={value.accountName}
            onChange={handlePersonChange}
            placeholder={value.departmentTitle ? '请选择下单人' : '请先选择科室'}
            allLabel="请选择下单人"
            searchPlaceholder="搜索下单人..."
            icon="ri-user-line"
          />
        </div>
      </div>

      <div className={fieldClass}>
        {labelEl(layout === 'grid' ? '创建日期' : '下单时间')}
        <div className={layout === 'grid' ? 'w-full max-w-[220px]' : 'w-[210px]'}>
          {layout === 'grid' ? (
            <DatePicker
              value={String(value.createdAt || '').slice(0, 10)}
              onChange={handleCreatedDateChange}
              disabled={disabled}
              placeholder="选择创建日期"
            />
          ) : (
            <DateTimePicker
              value={value.createdAt}
              onChange={handleCreatedAtChange}
              disabled={disabled}
              placeholder="选择下单时间"
            />
          )}
        </div>
      </div>
    </div>
  );
}
